// --- React and Hooks Imports ---
import React, { useState, useEffect, useRef } from 'react'; // useRef was missing in original but needed for poll emoji picker
import { createPortal } from 'react-dom'; // Needed for EmojiPicker

// --- Library Imports ---
import { X, Send, Video, Image, FileText, AlertTriangle, Check, BarChart3, Plus, Trash2, Smile } from 'lucide-react'; // Added icons for polls

// --- Local Imports ---
import {
    ENABLE_CONTENT_TYPE_TEXT, ENABLE_CONTENT_TYPE_VIDEO,
    ENABLE_CONTENT_TYPE_IMAGE, ENABLE_CONTENT_TYPE_POLL, // Added POLL
    DEFAULT_PLACEHOLDER_IMAGE, TEXT_COLOR_MODE,
    HEADLINE_MAX_CHARS, MESSAGE_MAX_WORDS,
    MAX_POLL_OPTIONS, MIN_POLL_OPTIONS // Added Poll config
} from './config.js';
import SlateEditor from './SlateEditor.tsx';
import { serializeHTML, isEditorEmpty, deserializeHTML } from './slateUtils.ts';
import EmojiPicker from './EmojiPicker.tsx'; // Import the existing EmojiPicker
import { IFormatting, INewShoutDataForModal, IPollOption as FrontendPollOptionSetting } from './EchoVoid.tsx'; // Import types from EchoVoid, these are more up-to-date

// --- Type Definitions ---
// This is the structure for an option being built in the modal
interface IPollOptionUI extends FrontendPollOptionSetting {
    // id is already in FrontendPollOptionSetting, used as key and for managing emoji picker
}

// The initialShoutState prop will use INewShoutDataForModal from EchoVoid
// The payload structure CreateShoutModal will emit is defined in CreateShoutModalProps

// --- Helper to calculate word count ---
const calculateWordCount = (textOrHtml: string): number => {
    if (!textOrHtml) return 0;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = textOrHtml;
    const text = tempDiv.textContent || tempDiv.innerText || "";
    const cleanedText = text.replace(/\s+/g, ' ').trim();
    return cleanedText.split(' ').filter(word => word.length > 0).length;
};

const MAX_POLL_OPTION_TEXT_LENGTH_FROM_CONFIG = (typeof MAX_POLL_OPTION_TEXT_LENGTH !== 'undefined') ? MAX_POLL_OPTION_TEXT_LENGTH : 60;

// --- Component Props ---
interface CreateShoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (payload: {
        headline: string;
        contentType: 'text' | 'video' | 'image' | 'poll'; // Added poll
        textContent: string | null;
        videoLink: string | null;
        imageUrl: string | null;
        formatting: IFormatting;
        poll_options_data?: { text: string, emoji: string }[]; // For poll submission
    }) => void;
    getThemeColors: () => { name: string; primary: string; secondary: string; hover: string; color: string };
    IS_ANDROID: boolean;
    youtubeUtils: {
        extractYouTubeVideoId: (url: string | null) => string | null;
        convertToEmbedUrl: (url: string | null) => string;
    };
    initialShoutState: INewShoutDataForModal; // Use the more comprehensive type from EchoVoid
}

// --- CreateShoutModal Component ---
const CreateShoutModal: React.FC<CreateShoutModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    getThemeColors,
    IS_ANDROID,
    youtubeUtils,
    initialShoutState
}) => {
    const [newShout, setNewShout] = useState<INewShoutDataForModal>(initialShoutState);
    const [messageWordCount, setMessageWordCount] = useState(0);

    const [showPollOptionEmojiPicker, setShowPollOptionEmojiPicker] = useState(false);
    const [emojiPickerPosition, setEmojiPickerPosition] = useState({ top: 0, left: 0 });
    const [currentPollOptionIdForEmoji, setCurrentPollOptionIdForEmoji] = useState<string | null>(null);
    const emojiButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    useEffect(() => {
        if (isOpen) {
            const currentMinPollOptions = MIN_POLL_OPTIONS || 2;
            const initialPollOpts = initialShoutState.contentType === 'poll' ?
                (initialShoutState.pollOptionsData && initialShoutState.pollOptionsData.length >= currentMinPollOptions ? initialShoutState.pollOptionsData : Array.from({ length: currentMinPollOptions }, (_, i) => ({ id: Date.now().toString() + `_${i}`, text: '', emoji: '', votes:0 })))
                : [];

            setNewShout({
                ...initialShoutState, // Spread the initial state from props
                // Override pollOptionsData specifically based on logic
                pollOptionsData: initialPollOpts.map((opt, index) => ({
                    ...(opt as FrontendPollOptionSetting), // Cast to ensure 'votes' is handled if present
                    id: opt.id || Date.now().toString() + `_${index}` // Ensure ID for UI key
                })) as IPollOptionUI[]
            });
            setMessageWordCount(calculateWordCount(initialShoutState.contentType === 'text' ? initialShoutState.textContent : ''));
        } else {
            setShowPollOptionEmojiPicker(false);
            setCurrentPollOptionIdForEmoji(null);
        }
    }, [isOpen, initialShoutState]);

    const handleSlateEditorChange = (htmlFromSlate: string) => {
        setNewShout(prev => ({ ...prev, textContent: htmlFromSlate }));
        setMessageWordCount(calculateWordCount(htmlFromSlate));
    };

    const handleAndroidTextareaChange = (plainText: string) => {
        setNewShout(prev => ({ ...prev, textContent: plainText }));
        setMessageWordCount(calculateWordCount(plainText));
    };

    const handlePollOptionChange = (id: string, field: 'text' | 'emoji', value: string) => {
        setNewShout(prev => ({
            ...prev,
            pollOptionsData: (prev.pollOptionsData as IPollOptionUI[]).map(option =>
                option.id === id ? { ...option, [field]: value } : option
            )
        }));
    };

    const addPollOption = () => {
        const currentMaxPollOptions = MAX_POLL_OPTIONS || 4;
        if (newShout.pollOptionsData.length < currentMaxPollOptions) {
            const newOption: IPollOptionUI = {
                id: Date.now().toString(),
                text: '',
                emoji: '',
                votes: 0,
            };
            setNewShout(prev => ({
                ...prev,
                pollOptionsData: [...(prev.pollOptionsData || []), newOption] as IPollOptionUI[]
            }));
        }
    };

    const removePollOption = (id: string) => {
        const currentMinPollOptions = MIN_POLL_OPTIONS || 2;
        if (newShout.pollOptionsData.length > currentMinPollOptions) {
            setNewShout(prev => ({
                ...prev,
                pollOptionsData: (prev.pollOptionsData as IPollOptionUI[]).filter(option => option.id !== id)
            }));
        }
    };

    const handlePollOptionEmojiPickerToggle = (optionId: string) => {
        const buttonRef = emojiButtonRefs.current[optionId];
        if (buttonRef) {
            const rect = buttonRef.getBoundingClientRect();
            const pickerWidth = 320;
            const pickerHeight = 400;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            let top = rect.bottom + window.scrollY + 5;
            if (spaceBelow < pickerHeight && spaceAbove > spaceBelow) {
                top = rect.top + window.scrollY - pickerHeight - 5;
            }
            let left = rect.left + window.scrollX;
            if (left + pickerWidth > window.innerWidth) {
                left = window.innerWidth - pickerWidth - 10;
            }
            left = Math.max(10, left);
            setEmojiPickerPosition({ top, left });
        }
        if (currentPollOptionIdForEmoji === optionId && showPollOptionEmojiPicker) {
            setShowPollOptionEmojiPicker(false);
            setCurrentPollOptionIdForEmoji(null);
        } else {
            setCurrentPollOptionIdForEmoji(optionId);
            setShowPollOptionEmojiPicker(true);
        }
    };

    const handleSelectPollOptionEmoji = (emoji: string) => {
        if (currentPollOptionIdForEmoji) {
            handlePollOptionChange(currentPollOptionIdForEmoji, 'emoji', emoji);
        }
        setShowPollOptionEmojiPicker(false);
        setCurrentPollOptionIdForEmoji(null);
    };

    const internalHandleSubmit = () => {
        let isTextContentConsideredEmpty = true;
        const currentTextContent = newShout.textContent; // Already from newShout which uses INewShoutDataForModal

        const currentHeadlineMaxChars = HEADLINE_MAX_CHARS || 100;
        const currentMessageMaxWords = MESSAGE_MAX_WORDS || 350;
        const currentMinPollOptions = MIN_POLL_OPTIONS || 2;
        const currentMaxPollOptions = MAX_POLL_OPTIONS || 4;

        if (newShout.contentType === 'text') {
            if (IS_ANDROID) {
                isTextContentConsideredEmpty = !currentTextContent.trim();
            } else {
                try {
                    const slateValue = deserializeHTML(currentTextContent);
                    isTextContentConsideredEmpty = isEditorEmpty(slateValue);
                } catch (e) {
                    isTextContentConsideredEmpty = !currentTextContent.replace(/<[^>]*>/g, '').trim();
                }
            }
        }

        if (!newShout.headline.trim()) { alert('Please enter a headline.'); return; }
        if (newShout.contentType === 'text' && isTextContentConsideredEmpty) { alert('Please enter some text content for your shout.'); return; }
        if (newShout.contentType === 'video' && !newShout.videoLink.trim()) { alert('Please enter a video link.'); return; }

        if (newShout.contentType === 'video') {
            const videoId = youtubeUtils.extractYouTubeVideoId(newShout.videoLink);
            if (!videoId) {
                alert('Please enter a valid YouTube URL.'); return;
            }
        }

        if (newShout.headline.length > currentHeadlineMaxChars) { alert(`Headline cannot exceed ${currentHeadlineMaxChars} characters.`); return; }
        if (newShout.contentType === 'text' && messageWordCount > currentMessageMaxWords) { alert(`Message cannot exceed ${currentMessageMaxWords} words.`); return; }

        if (newShout.contentType === 'poll') {
            if (newShout.pollOptionsData.length < currentMinPollOptions) {
                alert(`A poll must have at least ${currentMinPollOptions} options.`); return;
            }
            if (newShout.pollOptionsData.length > currentMaxPollOptions) {
                alert(`A poll can have at most ${currentMaxPollOptions} options.`); return;
            }
            for (const option of newShout.pollOptionsData) {
                if (!option.text.trim()) {
                    alert('All poll options must have text.'); return;
                }
                if (option.text.length > MAX_POLL_OPTION_TEXT_LENGTH_FROM_CONFIG) {
                    alert(`Poll option text cannot exceed ${MAX_POLL_OPTION_TEXT_LENGTH_FROM_CONFIG} characters. Problem with: "${option.text.slice(0,20)}..."`); return;
                }
            }
        }

        const payloadFormatting: IFormatting = { ...newShout.formatting };
        if (TEXT_COLOR_MODE === 'selected') { payloadFormatting.textColor = getThemeColors().color; }
        else if (TEXT_COLOR_MODE === 'white') { payloadFormatting.textColor = 'white'; }

        const videoLinkToSend = newShout.contentType === 'video' ? youtubeUtils.convertToEmbedUrl(newShout.videoLink) : null;
        let textContentForPayload: string | null = null;
        if (newShout.contentType === 'text') {
            if (isTextContentConsideredEmpty) {
                textContentForPayload = serializeHTML([{ type: 'paragraph', children: [{ text: '' }] }]);
            } else if (IS_ANDROID) {
                textContentForPayload = currentTextContent.split('\n').map(line =>
                    `<p>${line.replace(/&/g, "&").replace(/</g, "<").replace(/>/g, ">")}</p>`
                ).join('');
            } else {
                textContentForPayload = currentTextContent;
            }
        }
        
        const submissionPayload: Parameters<CreateShoutModalProps['onSubmit']>[0] = {
            headline: newShout.headline.trim(),
            contentType: newShout.contentType,
            textContent: textContentForPayload,
            videoLink: videoLinkToSend,
            imageUrl: newShout.contentType === 'image' ? (newShout.imageUrl?.trim() || DEFAULT_PLACEHOLDER_IMAGE) : null,
            formatting: payloadFormatting,
        };

        if (newShout.contentType === 'poll') {
            submissionPayload.poll_options_data = newShout.pollOptionsData.map(opt => ({
                text: opt.text,
                emoji: opt.emoji
            }));
        }
        onSubmit(submissionPayload);
    };

    if (!isOpen) return null;
    const theme = getThemeColors();
    const currentHeadlineMaxChars = HEADLINE_MAX_CHARS || 100;
    const currentMessageMaxWords = MESSAGE_MAX_WORDS || 350;
    const currentMinPollOptions = MIN_POLL_OPTIONS || 2;
    const currentMaxPollOptions = MAX_POLL_OPTIONS || 4;

    const isSubmitDisabled = () => {
        if (!newShout.headline.trim()) return true;
        if (newShout.contentType === 'text') {
            let emptyText = true;
            try {
                emptyText = IS_ANDROID ? !newShout.textContent.trim() : isEditorEmpty(deserializeHTML(newShout.textContent));
            } catch (e) {
                emptyText = !newShout.textContent?.replace(/<[^>]*>/g, '').trim();
            }
            if (emptyText || messageWordCount > currentMessageMaxWords) return true;
        }
        if (newShout.contentType === 'video') {
            if (!newShout.videoLink.trim() || (newShout.videoLink.length > 0 && !youtubeUtils.extractYouTubeVideoId(newShout.videoLink))) return true;
        }
        if (newShout.contentType === 'poll') {
            if (!newShout.pollOptionsData || newShout.pollOptionsData.length < currentMinPollOptions || newShout.pollOptionsData.length > currentMaxPollOptions) return true;
            if (newShout.pollOptionsData.some(opt => !opt.text.trim() || opt.text.length > MAX_POLL_OPTION_TEXT_LENGTH_FROM_CONFIG)) return true;
        }
        return false;
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
                <div className="bg-gray-800 rounded-lg max-w-2xl w-full shadow-xl flex flex-col"> {/* Removed sticky header/footer from inner container */}
                    <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800 z-10 rounded-t-lg"> {/* Sticky Header */}
                        <h2 className="text-xl font-bold">Create a New Shout</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors"> <X size={24} /> </button>
                    </div>

                    <div className="p-6 flex-grow max-h-[calc(90vh-140px)] overflow-y-auto custom-scrollbar-create"> {/* Scrollable Body */}
                        <div className="flex flex-col space-y-4">
                            <div> {/* Content Type Selection */}
                                <label className="block text-gray-300 mb-2 font-medium">Content Type</label>
                                <div className="flex space-x-2 flex-wrap gap-y-2">
                                    {ENABLE_CONTENT_TYPE_TEXT && (
                                        <button onClick={() => setNewShout(prev => ({ ...prev, contentType: 'text', pollOptionsData: [] }))} className={`px-4 py-2 rounded-full flex items-center gap-2 transition-colors duration-200 ${newShout.contentType === 'text' ? theme.primary + ' text-white shadow-md' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}>
                                            <FileText size={16} /> Text
                                        </button>
                                    )}
                                    {ENABLE_CONTENT_TYPE_VIDEO && (
                                        <button onClick={() => setNewShout(prev => ({ ...prev, contentType: 'video', pollOptionsData: [] }))} className={`px-4 py-2 rounded-full flex items-center gap-2 transition-colors duration-200 ${newShout.contentType === 'video' ? theme.primary + ' text-white shadow-md' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}>
                                            <Video size={16} /> Video
                                        </button>
                                    )}
                                    {ENABLE_CONTENT_TYPE_IMAGE && (
                                        <button onClick={() => setNewShout(prev => ({ ...prev, contentType: 'image', pollOptionsData: [] }))} className={`px-4 py-2 rounded-full flex items-center gap-2 transition-colors duration-200 ${newShout.contentType === 'image' ? theme.primary + ' text-white shadow-md' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}>
                                            <Image size={16} /> Image
                                        </button>
                                    )}
                                    {ENABLE_CONTENT_TYPE_POLL && (
                                        <button onClick={() => setNewShout(prev => ({ ...prev, contentType: 'poll', pollOptionsData: (prev.pollOptionsData && prev.pollOptionsData.length >= currentMinPollOptions) ? prev.pollOptionsData : Array.from({ length: currentMinPollOptions }, (_, i) => ({ id: Date.now().toString() + `_${i}`, text: '', emoji: '', votes:0 })) as IPollOptionUI[] }))} className={`px-4 py-2 rounded-full flex items-center gap-2 transition-colors duration-200 ${newShout.contentType === 'poll' ? theme.primary + ' text-white shadow-md' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}>
                                            <BarChart3 size={16} /> Poll
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div> {/* Headline Input */}
                                <div className="flex justify-between items-center mb-2">
                                    <label htmlFor="shout-headline" className="block text-gray-300 font-medium">Headline</label>
                                    <span className="text-gray-400 text-sm">{newShout.headline.length}/{currentHeadlineMaxChars} chars</span>
                                </div>
                                <input
                                    id="shout-headline" type="text" value={newShout.headline}
                                    onChange={e => setNewShout(prev => ({ ...prev, headline: e.target.value.slice(0, currentHeadlineMaxChars) }))}
                                    className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:border-gray-500 transition-colors duration-150"
                                    placeholder="What's the gist?" maxLength={currentHeadlineMaxChars} required
                                />
                            </div>

                            {/* Content Specific Inputs - Restored from Original */}
                            {newShout.contentType === 'text' && (
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-gray-300 font-medium">Message</label>
                                        <span className={`text-sm ${messageWordCount > currentMessageMaxWords ? 'text-red-400' : 'text-gray-400'}`}>
                                            {messageWordCount}/{currentMessageMaxWords} words
                                        </span>
                                    </div>
                                    {IS_ANDROID
                                        ? (
                                            <textarea
                                                id="shout-message-android"
                                                value={newShout.textContent}
                                                onChange={e => handleAndroidTextareaChange(e.target.value)}
                                                placeholder="What's on your mind?"
                                                className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:border-gray-500 transition-colors duration-150 min-h-[150px]"
                                            />
                                        )
                                        : (
                                            <SlateEditor
                                                key={`slate-${newShout.contentType}-${TEXT_COLOR_MODE}-${getThemeColors().name}`}
                                                initialHtml={newShout.textContent}
                                                onChange={handleSlateEditorChange}
                                                selectedThemeColor={getThemeColors().color}
                                            />
                                        )
                                    }
                                </div>
                            )}
                            {newShout.contentType === 'video' && (
                                <div>
                                    <label htmlFor="shout-video" className="block text-gray-300 mb-2 font-medium">YouTube Link</label>
                                    <div className="relative">
                                        <input
                                            id="shout-video" type="url" value={newShout.videoLink}
                                            onChange={e => setNewShout(prev => ({ ...prev, videoLink: e.target.value }))}
                                            className={`w-full bg-gray-700 border rounded p-2 text-white focus:outline-none transition-colors duration-150 pr-8 ${ newShout.videoLink && youtubeUtils.extractYouTubeVideoId(newShout.videoLink) ? 'border-green-500 focus:border-green-500' : newShout.videoLink && !youtubeUtils.extractYouTubeVideoId(newShout.videoLink) ? 'border-red-500 focus:border-red-500' : 'border-gray-600 focus:border-gray-500' }`}
                                            placeholder="Paste any YouTube link here..." required={newShout.contentType === 'video'}
                                        />
                                        {newShout.videoLink && (
                                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                {youtubeUtils.extractYouTubeVideoId(newShout.videoLink) ? (<Check size={20} className="text-green-400" />) : (<AlertTriangle size={20} className="text-red-400" />)}
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-gray-400 text-sm mt-1">Paste any YouTube link - we'll convert it automatically. Supports watch URLs, share links, and timestamps.</p>
                                    {!newShout.videoLink && (
                                        <div className="mt-2 text-xs text-gray-500">
                                            <p>Examples:</p>
                                            <ul className="list-disc list-inside ml-2">
                                                <li>https://www.youtube.com/watch?v=dQw4w9WgXcQ</li>
                                                <li>https://youtu.be/dQw4w9WgXcQ</li>
                                                <li>https://www.youtube.com/watch?v=dQw4w9WgXcQ&t=30s</li>
                                            </ul>
                                        </div>
                                    )}
                                    {newShout.videoLink && youtubeUtils.extractYouTubeVideoId(newShout.videoLink) && (
                                        <div className="mt-3">
                                            <div className="aspect-video w-full bg-gray-900 rounded overflow-hidden">
                                                <iframe src={youtubeUtils.convertToEmbedUrl(newShout.videoLink)} className="w-full h-full border-0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Video preview" ></iframe>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {newShout.contentType === 'image' && (
                                <div>
                                    <label className="block text-gray-300 mb-2 font-medium">Image</label>
                                    <p className="text-gray-400 mb-2 text-sm">Image uploading is not yet implemented. A placeholder will be used.</p>
                                    <div className="bg-gray-700 border border-dashed border-gray-600 rounded p-4 flex justify-center items-center min-h-[150px]">
                                        <img src={newShout.imageUrl || DEFAULT_PLACEHOLDER_IMAGE} alt="Placeholder" className="max-h-40 rounded object-contain" onError={e => ((e.target as HTMLImageElement).src = DEFAULT_PLACEHOLDER_IMAGE)} />
                                    </div>
                                </div>
                            )}
                            {/* End Restored Content Specific Inputs */}

                            {/* Poll Specific Inputs - Retained from previous correct version */}
                            {newShout.contentType === 'poll' && (
                                <div>
                                    <div className="flex justify-between items-center mb-3">
                                        <label className="block text-gray-300 font-medium">Build Poll Options</label>
                                        <span className="text-gray-400 text-sm">
                                            {newShout.pollOptionsData.length}/{currentMaxPollOptions} options
                                        </span>
                                    </div>
                                    <div className="space-y-3">
                                        {(newShout.pollOptionsData as IPollOptionUI[]).map((option, index) => (
                                            <div key={option.id} className="flex items-center gap-3">
                                                <button
                                                    ref={el => { if (el) emojiButtonRefs.current[option.id] = el; }}
                                                    type="button"
                                                    onClick={() => handlePollOptionEmojiPickerToggle(option.id)}
                                                    className="w-10 h-10 bg-gray-700 border border-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-600 transition-colors text-xl flex-shrink-0"
                                                    title="Select emoji for option"
                                                >
                                                    {option.emoji || <Smile size={18} className="text-gray-400"/>}
                                                </button>
                                                <div className="flex-1 relative">
                                                    <input
                                                        type="text"
                                                        value={option.text}
                                                        onChange={e => handlePollOptionChange(option.id, 'text', e.target.value.slice(0, MAX_POLL_OPTION_TEXT_LENGTH_FROM_CONFIG))}
                                                        className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:border-purple-500 transition-colors duration-150"
                                                        placeholder={`Option ${index + 1}`}
                                                        maxLength={MAX_POLL_OPTION_TEXT_LENGTH_FROM_CONFIG}
                                                    />
                                                    <span className="absolute right-2 bottom-1 text-xs text-gray-500 pointer-events-none">
                                                        {option.text.length}/{MAX_POLL_OPTION_TEXT_LENGTH_FROM_CONFIG}
                                                    </span>
                                                </div>
                                                {newShout.pollOptionsData.length > currentMinPollOptions && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removePollOption(option.id)}
                                                        className="text-red-400 hover:text-red-300 transition-colors p-1 flex-shrink-0"
                                                        title="Remove option"
                                                    >
                                                        <Trash2 size={20} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        {newShout.pollOptionsData.length < currentMaxPollOptions && (
                                            <button
                                                type="button"
                                                onClick={addPollOption}
                                                className="flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors py-2 px-3 rounded bg-gray-700/50 hover:bg-gray-700"
                                            >
                                                <Plus size={20} /> Add Option
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="p-4 border-t border-gray-700 flex justify-end sticky bottom-0 bg-gray-800 z-10 rounded-b-lg"> {/* Sticky Footer */}
                        <button
                            onClick={internalHandleSubmit}
                            disabled={isSubmitDisabled()}
                            className={`${theme.primary} ${theme.hover} px-6 py-2 rounded-full font-medium flex items-center transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-white`}
                        > <Send size={16} className="mr-2" /> Shout It Out </button>
                    </div>
                </div>
            </div>
            {showPollOptionEmojiPicker && currentPollOptionIdForEmoji && createPortal(
                <EmojiPicker
                    isOpen={showPollOptionEmojiPicker}
                    onClose={() => {
                        setShowPollOptionEmojiPicker(false);
                        setCurrentPollOptionIdForEmoji(null);
                    }}
                    onSelectEmoji={handleSelectPollOptionEmoji}
                    position={emojiPickerPosition}
                    userReactions={new Set()} // For poll option emoji, no prior "user reactions" apply
                />,
                document.body
            )}
            <style jsx>{`
                .custom-scrollbar-create::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar-create::-webkit-scrollbar-track { background: rgba(0,0,0,0.1); border-radius: 3px; }
                .custom-scrollbar-create::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 3px; }
                .custom-scrollbar-create::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
            `}</style>
        </>
    );
};

export default CreateShoutModal;
